import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SlideLayout } from '../../data/slides';
import {
  LayerId,
  moveLayer,
  reorderLayerOrder,
  resolveLayerOrder,
} from '../../data/layerOrder';
import { useLayerLabel } from '../../i18n/useLayerLabel';
import { appTheme } from '../../styles/theme';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Hint = styled.p`
  margin: 0 0 4px;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${appTheme.textMuted};
`;

const LayerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LayerRow = styled.div<{ $active: boolean; $dragging: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 8px 8px 6px;
  border-radius: ${appTheme.radiusSm};
  border: 1px solid ${({ $active }) => ($active ? appTheme.green : appTheme.border)};
  background: ${({ $active }) => ($active ? appTheme.mint : appTheme.surface)};
  opacity: ${({ $dragging }) => ($dragging ? 0.45 : 1)};
  cursor: grab;

  &:hover {
    border-color: ${appTheme.green};
  }
`;

const DragHandle = styled.span`
  flex-shrink: 0;
  width: 14px;
  color: ${appTheme.textMuted};
  font-size: 0.875rem;
  line-height: 1;
  user-select: none;
`;

const LayerButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${appTheme.text};
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LayerActions = styled.div`
  display: flex;
  gap: 2px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  width: 24px;
  height: 24px;
  border: 1px solid ${appTheme.border};
  border-radius: 6px;
  background: ${appTheme.surface};
  color: ${appTheme.textMuted};
  font-size: 0.75rem;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${appTheme.surfaceHover};
    color: ${appTheme.text};
    border-color: ${appTheme.green};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const BackgroundRow = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: ${appTheme.radiusSm};
  border: 1px solid ${({ $active }) => ($active ? appTheme.green : appTheme.border)};
  background: ${({ $active }) => ($active ? appTheme.mint : appTheme.surfaceHover)};
  font-size: 0.8125rem;
  color: ${appTheme.textMuted};
`;

interface LayersPanelProps {
  layout: SlideLayout;
  selectedLayerId: LayerId | null;
  onSelectLayer: (layerId: LayerId | null) => void;
  onLayerOrderChange: (layerOrder: LayerId[]) => void;
}

const LayersPanel: React.FC<LayersPanelProps> = ({
  layout,
  selectedLayerId,
  onSelectLayer,
  onLayerOrderChange,
}) => {
  const { t } = useTranslation();
  const layerLabel = useLayerLabel();
  const backToFront = useMemo(() => resolveLayerOrder(layout), [layout]);
  const frontToBack = useMemo(() => [...backToFront].reverse(), [backToFront]);

  const [dragId, setDragId] = useState<LayerId | null>(null);
  const [dropTargetId, setDropTargetId] = useState<LayerId | null>(null);

  const applyOrder = (nextBackToFront: LayerId[]) => {
    onLayerOrderChange(nextBackToFront);
  };

  const handleMove = (id: LayerId, direction: 'forward' | 'backward' | 'front' | 'back') => {
    applyOrder(moveLayer(backToFront, id, direction));
    onSelectLayer(id);
  };

  const handleDrop = (targetId: LayerId) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      setDropTargetId(null);
      return;
    }

    const fromIndex = backToFront.indexOf(dragId);
    const toIndex = backToFront.indexOf(targetId);
    if (fromIndex === -1 || toIndex === -1) {
      setDragId(null);
      setDropTargetId(null);
      return;
    }

    applyOrder(reorderLayerOrder(backToFront, fromIndex, toIndex));
    onSelectLayer(dragId);
    setDragId(null);
    setDropTargetId(null);
  };

  const backgroundLabel = layerLabel(layout, 'background');

  return (
    <Panel>
      <Hint>{t('editor.layers.hint')}</Hint>
      <LayerList>
        {frontToBack.map((layerId) => {
          const backIndex = backToFront.indexOf(layerId);
          const isActive = selectedLayerId === layerId;
          const label = layerLabel(layout, layerId);

          return (
            <LayerRow
              key={layerId}
              $active={isActive || dropTargetId === layerId}
              $dragging={dragId === layerId}
              draggable
              onDragStart={() => setDragId(layerId)}
              onDragEnd={() => {
                setDragId(null);
                setDropTargetId(null);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDropTargetId(layerId);
              }}
              onDrop={(event) => {
                event.preventDefault();
                handleDrop(layerId);
              }}
            >
              <DragHandle aria-hidden>⋮⋮</DragHandle>
              <LayerButton
                type="button"
                $active={isActive}
                onClick={() => onSelectLayer(layerId)}
              >
                {label}
              </LayerButton>
              <LayerActions>
                <IconButton
                  type="button"
                  aria-label={t('editor.layers.sendBackward', { label })}
                  disabled={backIndex <= 0}
                  onClick={() => handleMove(layerId, 'backward')}
                >
                  ↓
                </IconButton>
                <IconButton
                  type="button"
                  aria-label={t('editor.layers.bringForward', { label })}
                  disabled={backIndex >= backToFront.length - 1}
                  onClick={() => handleMove(layerId, 'forward')}
                >
                  ↑
                </IconButton>
              </LayerActions>
            </LayerRow>
          );
        })}
      </LayerList>
      <BackgroundRow $active={selectedLayerId === 'background'}>
        <DragHandle aria-hidden style={{ opacity: 0.35 }}>
          ⋮⋮
        </DragHandle>
        <LayerButton type="button" onClick={() => onSelectLayer('background')}>
          {t('editor.layers.alwaysBack', { label: backgroundLabel })}
        </LayerButton>
      </BackgroundRow>
    </Panel>
  );
};

export default LayersPanel;
